import { bffCrmPersonChatSummary } from '@fireflyhealth/api'
import type { BffCrmPersonChatSummaryRetrieveResponse } from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import { required } from '~/shared/lib/required'
// Local alias: the hook returns it inferred, so nothing outside this module needs the
// name. Export it when a consumer actually wants to annotate with it.
type PersonChatSummary = BffCrmPersonChatSummaryRetrieveResponse

/** Statuses a job will not move on from, so polling stops. */
const TERMINAL_STATUSES: PersonChatSummary['status'][] = ['complete', 'skipped', 'failed']

const isTerminal = (status: PersonChatSummary['status']) => TERMINAL_STATUSES.includes(status)

const POLL_INTERVAL_MILLIS = 2000

const getPersonChatSummaryKey = (personId: number) => ['getPersonChatSummary', personId]

const pollPersonChatSummaryKey = (personId: number, summaryId: number) => [
  'pollPersonChatSummary',
  personId,
  summaryId,
]

/**
 * A chat summary for a person, generating one if there is nothing fresh to serve.
 *
 * The endpoint is get-or-create: it reuses a summary still checkpointed at the person's
 * newest message, joins a job already running, and otherwise queues one. So this fetches
 * once and then polls by id until the job settles, rather than re-posting — re-posting
 * would queue a fresh job every interval once one had failed, because a failed summary is
 * never reused.
 */
export const usePersonChatSummary = (personId: number) => {
  const created = useQuery<PersonChatSummary>(
    getPersonChatSummaryKey(personId),
    () => bffCrmPersonChatSummary.create({ person_id: personId }).then(r => r.data),
    { retry: false },
  )

  const pendingSummaryId =
    created.data && !isTerminal(created.data.status) ? created.data.id : undefined

  const polled = useQuery<PersonChatSummary>(
    pendingSummaryId != null
      ? pollPersonChatSummaryKey(personId, pendingSummaryId)
      : ['pollPersonChatSummary', personId, null],
    () =>
      bffCrmPersonChatSummary
        .retrieve({
          person_id: personId,
          summary_id: required(pendingSummaryId, 'a pending summary id'),
        })
        .then(r => r.data),
    {
      enabled: pendingSummaryId != null,
      retry: false,
      refetchInterval: query =>
        isTerminal(query.state.data?.status ?? null) ? false : POLL_INTERVAL_MILLIS,
    },
  )

  const summary = polled.data ?? created.data ?? null

  return {
    data: summary,
    // Still loading while the job has not settled, so the card keeps its skeleton
    // rather than flashing an empty summary.
    isLoading: created.isPending || (summary != null && !isTerminal(summary.status)),
    error: created.error ?? polled.error,
    refetch: created.refetch,
  }
}
